/**
 * 全国态势地图 —— 主容器。
 *
 * 用 React 重写原型「全国项目态势地图」并接入真实后端接口：
 *   顶部 KPI 条 + SVG 中国地图（省轮廓 + 项目光点）+ 左侧筛选 + 右侧排行 + 图例。
 *
 * 关键联动：点击光点 → setCurrentProjectId(id) + 跳转 /star。
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { HEALTH_COLORS } from '@atlaspm/shared';
import { useUiStore } from '../../stores/ui';
import { useAuthStore } from '../../stores/auth';
import Spinner from '../../components/Spinner';
import EmptyState from '../../components/EmptyState';
import ProjectFormDialog from '../project/ProjectFormDialog';
import DeleteProjectDialog from '../project/DeleteProjectDialog';
import type { ProjectDetail } from '../project/api';
import { useMapData, buildNationalKpis, type MapProject, type ProvinceAgg } from './useMapData';
import { levelColor } from './colors';
import { projectTypeLabel } from '../project/api';
import { createProjection, project } from './projection';
import ChinaMap, { type MapTransform } from './ChinaMap';
import KpiBar from './KpiBar';
import HoverCard from './HoverCard';
import ProvinceRank, { type RankMode, type RankRow } from './ProvinceRank';
import FilterPanel from './FilterPanel';
import Legend from './Legend';
import './map.css';

const MIN_SCALE = 0.7;
const MAX_SCALE = 5;

export default function MapView() {
  const navigate = useNavigate();
  const setCurrentProjectId = useUiStore((s) => s.setCurrentProjectId);
  const filters = useUiStore((s) => s.filters);
  const setFilter = useUiStore((s) => s.setFilter);
  const resetFilters = useUiStore((s) => s.resetFilters);

  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canCreateProject = hasPermission('project:create');
  const canUpdateProject = hasPermission('project:update');
  const canDeleteProject = hasPermission('project:delete');

  const data = useMapData();
  const { geo, projects, provinces } = data;

  const [transform, setTransform] = useState<MapTransform>({ x: 0, y: 0, k: 1 });
  const [rankMode, setRankMode] = useState<RankMode>('health');
  const [hover, setHover] = useState<{ project: MapProject; x: number; y: number } | null>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  /* ── ⭐ P6.54 项目搜索（按名称 / 代码，遵守数据范围）── */
  const [search, setSearch] = useState('');
  /** 被搜索定位的项目 id（地图加聚焦环） */
  const [highlightId, setHighlightId] = useState<string | null>(null);

  /* ── 项目新建 / 编辑 / 删除（全部从态势图发起，不再有独立列表页） ── */
  const [editor, setEditor] = useState<{ mode: 'create' | 'edit'; projectId?: string } | null>(
    null,
  );
  const [toDelete, setToDelete] = useState<MapProject | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  /** 新建成功后待定位的经纬度 */
  const [focus, setFocus] = useState<{ lng: number; lat: number } | null>(null);
  const hoverTimer = useRef<number | null>(null);

  const stageRef = useRef<HTMLDivElement>(null);
  const drag = useRef({ active: false, moved: false, sx: 0, sy: 0, ox: 0, oy: 0 });
  const suppressClick = useRef(false);

  /* ── 尺寸测量（响应式） ── */
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const update = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* ── 滚轮缩放（原生监听，禁用页面滚动） ── */
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const mx = e.clientX - r.left;
      const my = e.clientY - r.top;
      setTransform((t) => {
        const k1 = Math.max(MIN_SCALE, Math.min(MAX_SCALE, t.k * (e.deltaY > 0 ? 0.9 : 1.11)));
        const ratio = k1 / t.k;
        return { k: k1, x: mx - (mx - t.x) * ratio, y: my - (my - t.y) * ratio };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  /* ── 拖拽平移 ── */
  const onStagePointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    drag.current = { active: true, moved: false, sx: e.clientX, sy: e.clientY, ox: 0, oy: 0 };
    drag.current.ox = transform.x;
    drag.current.oy = transform.y;
    e.currentTarget.setPointerCapture(e.pointerId);
    e.currentTarget.classList.add('grabbing');
  }, [transform.x, transform.y]);

  const onStagePointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d.active) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (Math.abs(dx) + Math.abs(dy) > 3) {
      d.moved = true;
      setHover(null);
    }
    setTransform((t) => ({ ...t, x: d.ox + dx, y: d.oy + dy }));
  }, []);

  const onStagePointerUp = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d.active) return;
    d.active = false;
    suppressClick.current = d.moved;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    e.currentTarget.classList.remove('grabbing');
  }, []);

  /* ── 省下钻 ── */
  const handleProvinceClick = useCallback(
    (code: string) => {
      if (suppressClick.current) {
        suppressClick.current = false;
        return;
      }
      setFilter('provinceCode', filters.provinceCode === code ? undefined : code);
    },
    [setFilter, filters.provinceCode],
  );

  const onStageClick = useCallback(() => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    if (filters.provinceCode) setFilter('provinceCode', undefined);
  }, [setFilter, filters.provinceCode]);

  /* ── 光点交互 ── */
  const openStar = useCallback(
    (p: MapProject) => {
      setCurrentProjectId(p.id);
      navigate('/star');
    },
    [navigate, setCurrentProjectId],
  );

  /**
   * 悬浮卡交互（⭐ P6.64 重构）。
   *
   * 卡片【固定在光点位置】而非跟随鼠标：
   *   · handleDotEnter 取光点 DOM 的【包围盒中心】（视口坐标）作为锚点；
   *   · 不再有鼠标移动回调 —— 鼠标微动不再更新坐标，从根上消除闪烁；
   *   · 离开光点延时 260ms 关闭；鼠标移入卡片时由 handleCardEnter 取消关闭。
   *
   * ⚠️ 光点整组（光晕 + 双环 + 核心）的包围盒中心即光点中心，
   *    用 getBoundingClientRect 取，兼容地图平移 / 缩放后的任意位置。
   */
  const clearHoverTimer = useCallback(() => {
    if (hoverTimer.current !== null) {
      window.clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  }, []);

  const closeHover = useCallback(() => {
    clearHoverTimer();
    setHover(null);
  }, [clearHoverTimer]);

  const handleDotEnter = useCallback(
    (project: MapProject, e: ReactMouseEvent) => {
      clearHoverTimer();
      const r = e.currentTarget.getBoundingClientRect();
      setHover({ project, x: r.left + r.width / 2, y: r.top + r.height / 2 });
    },
    [clearHoverTimer],
  );
  const handleDotLeave = useCallback(() => {
    clearHoverTimer();
    hoverTimer.current = window.setTimeout(() => setHover(null), 260);
  }, [clearHoverTimer]);
  const handleCardEnter = useCallback(() => clearHoverTimer(), [clearHoverTimer]);
  const handleCardLeave = useCallback(() => closeHover(), [closeHover]);

  useEffect(() => () => clearHoverTimer(), [clearHoverTimer]);

  /* ── 弹窗动作 ── */
  const openCreate = useCallback(() => {
    closeHover();
    setEditor({ mode: 'create' });
  }, [closeHover]);

  const handleEdit = useCallback(
    (p: MapProject) => {
      closeHover();
      setEditor({ mode: 'edit', projectId: p.id });
    },
    [closeHover],
  );

  const handleDelete = useCallback(
    (p: MapProject) => {
      closeHover();
      setToDelete(p);
    },
    [closeHover],
  );

  const handleSaved = useCallback(
    (p: ProjectDetail) => {
      const wasCreate = editor?.mode === 'create';
      setEditor(null);
      if (wasCreate) {
        setFocus({ lng: p.longitude, lat: p.latitude });
        setToast(`已创建「${p.name}」，地图已定位`);
      } else {
        setToast(`已保存「${p.name}」`);
      }
    },
    [editor],
  );

  /* 新建成功后把地图中心移到新项目（数据刷新由 useCreateProject 失效缓存完成） */
  useEffect(() => {
    if (!focus || size.w === 0 || size.h === 0) return;
    const proj = createProjection(size.w, size.h);
    const [px, py] = project(proj, focus.lng, focus.lat);
    const k = 1.6;
    setTransform({ x: size.w / 2 - px * k, y: size.h / 2 - py * k, k });
    setFocus(null);
  }, [focus, size.w, size.h]);

  /* 提示条自动消失 */
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 5000);
    return () => window.clearTimeout(t);
  }, [toast]);

  /* ── 任务形态筛选：先求出「含该形态任务」的 projectId 集合 ── */
  const projectIdsWithForm = useMemo(() => {
    if (!filters.taskForm) return null;
    const ids = new Set<string>();
    for (const t of data.tasks) {
      if (t.categoryName === filters.taskForm) ids.add(t.projectId);
    }
    return ids;
  }, [filters.taskForm, data.tasks]);

  /* ── 过滤后的项目（健康状态 / 项目类型 / 任务形态 / 下钻省份） ── */
  const filteredProjects = useMemo(
    () =>
      projects.filter((p) => {
        if (filters.healthLevel && p.level !== filters.healthLevel) return false;
        if (filters.projectType && p.projectType !== filters.projectType) return false;
        if (projectIdsWithForm && !projectIdsWithForm.has(p.id)) return false;
        if (filters.provinceCode && p.provinceCode !== filters.provinceCode) return false;
        return true;
      }),
    [projects, filters.healthLevel, filters.projectType, projectIdsWithForm, filters.provinceCode],
  );

  /* ── ⭐ P6.54 搜索：匹配名称 / 代码（大小写不敏感），仅在可见数据范围内 ── */
  const normalizedSearch = search.trim().toLowerCase();
  const searchMatchIds = useMemo(() => {
    if (!normalizedSearch) return null;
    const ids = new Set<string>();
    for (const p of projects) {
      if (
        p.name.toLowerCase().includes(normalizedSearch) ||
        p.code.toLowerCase().includes(normalizedSearch)
      ) {
        ids.add(p.id);
      }
    }
    return ids;
  }, [projects, normalizedSearch]);

  const searchResults = useMemo(() => {
    if (!normalizedSearch || !searchMatchIds) return [];
    return projects
      .filter((p) => searchMatchIds.has(p.id))
      .slice(0, 10)
      .map((p) => ({ id: p.id, name: p.name, code: p.code, provinceName: p.provinceName }));
  }, [projects, normalizedSearch, searchMatchIds]);

  /** 把地图中心移到指定项目并放大（受控定位） */
  const locateProject = useCallback(
    (p: MapProject) => {
      if (size.w === 0 || size.h === 0) return;
      const proj = createProjection(size.w, size.h);
      const [px, py] = project(proj, p.longitude, p.latitude);
      const k = 2.2;
      setTransform({ x: size.w / 2 - px * k, y: size.h / 2 - py * k, k });
      setHighlightId(p.id);
    },
    [size.w, size.h],
  );

  /** 点搜索结果：清掉可能遮挡该项目的筛选，然后地图定位聚焦 */
  const selectSearchResult = useCallback(
    (id: string) => {
      const p = projects.find((x) => x.id === id);
      if (!p) return;
      resetFilters();
      locateProject(p);
      setToast(`已定位「${p.name}」`);
    },
    [projects, resetFilters, locateProject],
  );

  const openSearchResultStar = useCallback(
    (id: string) => {
      const p = projects.find((x) => x.id === id);
      if (p) openStar(p);
    },
    [projects, openStar],
  );

  /* ── 7 张 KPI 卡：随筛选实时变化 ── */
  const kpis = useMemo(
    () =>
      buildNationalKpis({
        projects: filteredProjects,
        tasks: data.tasks,
        approvals: data.approvals,
      }),
    [filteredProjects, data.tasks, data.approvals],
  );

  /* 项目类型选项：值来自真实数据（保持 code），label 用字典名称映射 */
  const projectTypeOptions = useMemo(
    () =>
      data.projectTypes.map((t) => ({
        value: t,
        label: data.projectTypeLabels[t] ?? projectTypeLabel(t),
      })),
    [data.projectTypes, data.projectTypeLabels],
  );

  const selectedProvince = useMemo(
    () => provinces.find((p) => p.provinceCode === filters.provinceCode),
    [provinces, filters.provinceCode],
  );

  /* ── 排行 ── */
  const rankRows = useMemo<RankRow[]>(
    () =>
      selectedProvince
        ? projectRows(filteredProjects.filter((p) => p.provinceCode === selectedProvince.provinceCode), rankMode)
        : provinceRows(provinces, rankMode),
    [selectedProvince, filteredProjects, provinces, rankMode],
  );

  const handleRankRowClick = useCallback(
    (key: string) => {
      if (selectedProvince) {
        const p = projects.find((x) => x.id === key);
        if (p) openStar(p);
      } else {
        setFilter('provinceCode', key);
      }
    },
    [selectedProvince, projects, openStar, setFilter],
  );

  const showEmpty = !data.isLoading && filteredProjects.length === 0;

  return (
    <div className="mapview">
      {/* 顶部 KPI 横条（原型 #kpibar） */}
      <KpiBar items={kpis} />

      {/* 地图舞台 */}
      <div
        ref={stageRef}
        className="map-stage"
        onPointerDown={onStagePointerDown}
        onPointerMove={onStagePointerMove}
        onPointerUp={onStagePointerUp}
        onPointerCancel={onStagePointerUp}
        onClick={onStageClick}
      >
        {size.w > 0 && size.h > 0 && (
          <ChinaMap
            width={size.w}
            height={size.h}
            geo={geo}
            projects={filteredProjects}
            provinces={provinces}
            transform={transform}
            selectedProvinceCode={filters.provinceCode}
            searchMatchIds={searchMatchIds}
            highlightProjectId={highlightId}
            onProvinceClick={handleProvinceClick}
            onDotEnter={handleDotEnter}
            onDotLeave={handleDotLeave}
            onDotClick={openStar}
          />
        )}

        {data.isLoading ? (
          <div className="map-overlay">
            <Spinner label="正在加载全国态势数据…" />
          </div>
        ) : null}

        {data.isError ? (
          <div className="map-overlay">
            <EmptyState
              icon="⚠"
              title="数据加载失败"
              hint={data.error instanceof Error ? data.error.message : '请检查后端服务'}
            />
          </div>
        ) : null}

        {showEmpty && !data.isError ? (
          <div className="map-noresult">当前筛选条件下没有项目</div>
        ) : null}
      </div>

      {/*
        左侧列：筛选 + 健康度语义。
        P6.69：二者放入同一纵向 flex 容器（容器高度 = 面板区可用高度），
        筛选面板可收缩并内部滚动，图例固定在列底 —— 窗口上下变矮时
        两个卡片【永不重叠】，且都保持可见。
      */}
      <div className="map-leftcol">
        {/* 左侧筛选（原型 #filter：健康状态 / 项目类型 / 任务形态） */}
        <FilterPanel
          healthLevel={filters.healthLevel}
          projectType={filters.projectType}
          taskForm={filters.taskForm}
          onHealthLevel={(v) => setFilter('healthLevel', v)}
          onProjectType={(v) => setFilter('projectType', v)}
          onTaskForm={(v) => setFilter('taskForm', v)}
          projectTypeOptions={projectTypeOptions}
          taskFormOptions={data.taskForms}
          onCreateProject={canCreateProject ? openCreate : undefined}
          searchValue={search}
          onSearchChange={setSearch}
          onSearchClear={() => setSearch('')}
          searchResults={searchResults}
          onSelectSearchResult={selectSearchResult}
          onOpenSearchResultStar={openSearchResultStar}
        />

        {/* 图例（原型 #legend：健康度语义） */}
        <Legend />
      </div>

      {/* 右侧：省份排行（原型 #rank） */}
      <ProvinceRank
        title={selectedProvince ? `${selectedProvince.provinceName} · 项目` : '全国省份排行'}
        mode={rankMode}
        onModeChange={setRankMode}
        rows={rankRows}
        onRowClick={handleRankRowClick}
      />

      {/* 悬浮卡（含编辑 / 删除入口） */}
      {hover ? (
        <HoverCard
          project={hover.project}
          anchor={{ x: hover.x, y: hover.y }}
          projectTypeName={
            data.projectTypeLabels[hover.project.projectType] ??
            projectTypeLabel(hover.project.projectType)
          }
          canEdit={canUpdateProject}
          canDelete={canDeleteProject}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onPointerEnter={handleCardEnter}
          onPointerLeave={handleCardLeave}
        />
      ) : null}

      {/* 新建 / 编辑项目弹窗（表单复用 ProjectForm） */}
      <ProjectFormDialog
        open={editor !== null}
        mode={editor?.mode ?? 'create'}
        projectId={editor?.projectId}
        onClose={() => setEditor(null)}
        onSaved={handleSaved}
      />

      {/* 删除二次确认（复用 DeleteProjectDialog） */}
      <DeleteProjectDialog
        project={toDelete}
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onDeleted={() => setToDelete(null)}
      />

      {/* 创建 / 保存成功提示 */}
      {toast ? (
        <div className="map-toast" data-testid="map-toast" role="status">
          <span className="map-toast__icon" aria-hidden="true">
            ✔
          </span>
          <span className="map-toast__text">{toast}</span>
          <button
            type="button"
            className="map-toast__close"
            aria-label="关闭提示"
            onClick={() => setToast(null)}
          >
            ✕
          </button>
        </div>
      ) : null}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   排行行构造
   ═══════════════════════════════════════════════════════════ */

function provinceRows(provinces: readonly ProvinceAgg[], mode: RankMode): RankRow[] {
  const rows = provinces.map((p) => {
    const dormant = p.level === 'IDLE' || p.level === 'FROZEN';
    if (mode === 'overdue') {
      return {
        key: p.provinceCode,
        name: p.provinceName,
        value: p.overdueCount,
        display: String(p.overdueCount),
        color: p.overdueCount > 8 ? HEALTH_COLORS.ALERT : p.overdueCount > 3 ? HEALTH_COLORS.WARNING : HEALTH_COLORS.HEALTHY,
        sub: `${p.count} 项目 · ${p.taskCount} 任务`,
      };
    }
    if (mode === 'pass') {
      const rate = p.metricPassRate;
      const pct = rate === null ? null : rate * 100;
      return {
        key: p.provinceCode,
        name: p.provinceName,
        value: pct ?? 0,
        display: pct === null ? '—' : pct.toFixed(0),
        color: pct === null ? levelColor('IDLE') : pct >= 85 ? HEALTH_COLORS.HEALTHY : pct >= 70 ? HEALTH_COLORS.WARNING : HEALTH_COLORS.ALERT,
        sub: `${p.count} 项目 · ${p.taskCount} 任务`,
      };
    }
    return {
      key: p.provinceCode,
      name: p.provinceName,
      value: dormant ? 0 : p.avgHealth,
      display: dormant ? '—' : p.avgHealth.toFixed(1),
      color: levelColor(p.level),
      sub: `${p.count} 项目 · ${p.taskCount} 任务`,
    };
  });
  return rows.sort((a, b) => b.value - a.value);
}

function projectRows(projects: readonly MapProject[], mode: RankMode): RankRow[] {
  const rows = projects.map((p) => {
    const dormant = p.level === 'IDLE' || p.level === 'FROZEN';
    if (mode === 'overdue') {
      return {
        key: p.id,
        name: p.name,
        value: p.overdueTaskCnt,
        display: String(p.overdueTaskCnt),
        color: p.overdueTaskCnt > 0 ? HEALTH_COLORS.ALERT : HEALTH_COLORS.HEALTHY,
        sub: `${p.overdueTaskCnt} 逾期`,
      };
    }
    if (mode === 'pass') {
      const pct = p.metricPassRate === null ? null : p.metricPassRate * 100;
      return {
        key: p.id,
        name: p.name,
        value: pct ?? 0,
        display: pct === null ? '—' : pct.toFixed(0),
        color: pct === null ? levelColor('IDLE') : pct >= 85 ? HEALTH_COLORS.HEALTHY : pct >= 70 ? HEALTH_COLORS.WARNING : HEALTH_COLORS.ALERT,
        sub: `${p.taskCount} 任务`,
      };
    }
    return {
      key: p.id,
      name: p.name,
      value: dormant ? 0 : p.healthScore,
      display: dormant ? '—' : p.healthScore.toFixed(1),
      color: levelColor(p.level),
      sub: `${p.taskCount} 任务`,
    };
  });
  return rows.sort((a, b) => b.value - a.value);
}
